import React, {useState, useEffect} from "react"

import {RenewalQuoteStatus, ZuoraCurrentPlanWindow} from "@irati/types"
import FormattedMessage, {InjectIntl, injectIntl} from "@irati/utils/lang"
import {H2} from "@irati/ui/src/Typography"
import mainTheme from "@irati/ui/src/global/theme"
import Button from "@irati/ui/src/Button"
import Flex from "@irati/ui/src/Flex"
import intercom from "@irati/utils/intercom"
import GenericModal from "@irati/ui/src/GenericModal"
import adminTracking, {RenewalPlanType, RenewalPlanPage, RenewalPlanSource} from "@irati-tracking/admin/admin"
import FilterChipToggle from "@irati-web/admin/src/components/FilterChipToggle"

const CHANGE_PLAN_OPTIONS: ChangePlanOption[] = [
  {
    value: "moreSeats",
    label: "admin.billing.currentPlan.modal.options.moreSeats"
  },
  {
    value: "reduceSeats",
    label: "admin.billing.currentPlan.modal.options.reduceSeats"
  },
  {
    value: "flexiPoints",
    label: "admin.billing.currentPlan.modal.options.flexiPoints"
  },
  {
    value: "reduceEAP",
    label: "admin.billing.currentPlan.modal.options.reduceEAP"
  },
  {
    value: "addEAP",
    label: "admin.billing.currentPlan.modal.options.addEAP"
  },
  {
    value: "upgradeEAP",
    label: "admin.billing.currentPlan.modal.options.upgradeEAP"
  },
  {
    value: "downgradeEAP",
    label: "admin.billing.currentPlan.modal.options.downgradeEAP"
  },
  {
    value: "addMedical",
    label: "admin.billing.currentPlan.modal.options.addMedical"
  },
  {
    value: "removeMedical",
    label: "admin.billing.currentPlan.modal.options.removeMedical"
  },
  {
    value: "other",
    label: "admin.billing.currentPlan.modal.options.other"
  }
]

interface ChangePlanOption {
  value: string
  label: string
}

interface Props extends InjectIntl {
  show: boolean
  renewalStatus?: RenewalQuoteStatus
  renewalWindow?: ZuoraCurrentPlanWindow
  onHide: (ignoreTracking: boolean) => void
  onCancel: () => void
  source: RenewalPlanSource
}

const ChangePlanModal: React.FC<Props> = ({show, renewalWindow, renewalStatus, onHide, onCancel, source, formatMessage}) => {
  const [showModal, setShowModal] = useState<boolean>(show)
  const [selectedOption, setSelectedOption] = useState<string>()

  const handleConfirm = () => {
    const option = CHANGE_PLAN_OPTIONS.find((op) => op.value === selectedOption)
    adminTracking.renewalPlan({
      stage: 2,
      type: RenewalPlanType.changePlanFlow,
      page: RenewalPlanPage.changeplanStage,
      reason: option?.value,
      action: "submitChangePlan",
      source
    })
    intercom.addCustomMessage(formatMessage({id: option?.label}))
    reset()
  }

  const handleCancel = () => {
    adminTracking.renewalPlan({
      stage: 2,
      type: RenewalPlanType.cancellationFlow,
      action: "cancelMyPlan",
      source
    })
    reset()
    onCancel()
  }

  const reset = () => {
    setSelectedOption(undefined)
    onHide(true)
  }

  useEffect(() => {
    setShowModal(show)
  }, [show])

  return (
    <GenericModal customWidth={800} hideActions show={showModal} onHide={reset}>
      <H2 marginTop={2} marginBottom={2} color={mainTheme.colors.filterChip}>
        <FormattedMessage id="admin.billing.currentPlan.modal.header" />
      </H2>
      <Flex minWidth="700px" wrap="wrap" gap={mainTheme.spacing.one} marginBottom={3}>
        {CHANGE_PLAN_OPTIONS.map((option: ChangePlanOption, i) => (
          <FilterChipToggle key={i} isToggled={selectedOption === option.value} onClick={() => setSelectedOption(option.value)}>
            {formatMessage({id: option.label})}
          </FilterChipToggle>
        ))}
      </Flex>
      <Flex justifyContent="space-between" alignItems="center">
        <Button primary onClick={handleConfirm} disabled={!selectedOption}>
          <FormattedMessage id="admin.billing.currentPlan.modal.btn.confirm" />
        </Button>
        {renewalWindow !== ZuoraCurrentPlanWindow.Ongoing && renewalStatus === RenewalQuoteStatus.Available && (
          <Button cta onClick={handleCancel}>
            <FormattedMessage id="admin.billing.currentPlan.modal.btn.cancel" />
          </Button>
        )}
      </Flex>
    </GenericModal>
  )
}

export default injectIntl(ChangePlanModal)
